import type React from 'react';
import { useTranslation } from 'react-i18next';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ActionCatalog,
    ActionSearchBar,
    PinnedActions,
    RecentActions,
} from './components';
import type { UseControlCenterPageResult } from './useControlCenterPage';

type ControlCenterPageViewProps = {
    page: UseControlCenterPageResult;
};

/**
 * The Control Center, top to bottom: one sentence, the search field, the pinned
 * grid, what was reached for last, and the catalog.
 *
 * The order is the argument. Search is first because it is the fastest route
 * and the reason everything below it can stay quiet; pinned is second because
 * six tiles answer most days; the catalog is last because it is the fallback,
 * not the destination.
 */
export const ControlCenterPageView: React.FC<ControlCenterPageViewProps> = ({
    page,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex w-full max-w-6xl flex-col gap-6"
            {...getNodeTestIdProp('control-center-page')}
        >
            <p className="text-sm text-muted">{t('controlCenter.intro')}</p>

            <ActionSearchBar
                onOpen={page.onOpenPalette}
                totalCount={page.totalCount}
            />

            <PinnedActions
                actions={page.pinned}
                isEditing={page.isEditingPins}
                onMove={page.onMovePin}
                onReorder={page.onReorderPin}
                onRun={page.onRunAction}
                onToggleEditing={page.onToggleEditPins}
                onUnpin={page.onTogglePin}
                refusals={page.refusals}
            />

            <RecentActions
                actions={page.recent}
                onRun={page.onRunAction}
                refusals={page.refusals}
            />

            <ActionCatalog
                areaCount={page.areaCount}
                availableCount={page.availableCount}
                expandedAreas={page.expandedAreas}
                isCompact={page.isCompact}
                isPinned={page.isPinned}
                onRun={page.onRunAction}
                onExpandedAreasChange={page.onExpandedAreasChange}
                onTogglePin={page.onTogglePin}
                refusals={page.refusals}
                sections={page.sections}
            />
        </div>
    );
};
